import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import HeaderWithBreadcrumbs from '../../componentsV2/Headers/HeaderWithBreadcrumbs';
import { DialogsProvider, ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import ImportPage from './ImportPage';
import { getImport, downloadImportFile } from '../../utils/api/imports';
import { useToasts } from '../../hooks';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const BATCH_SIZE = 50;

const ImportPageWrapper = ({
  importId, filename, organizationId,
}) => {
  const { showToast } = useToasts();
  const fileName = filename || '';
  const [importedFile, setImportedFile] = useState();
  const [allEntries, setAllEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [hrefLink, setHrefLink] = useState(null);
  const isFetchingRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const { getFromLocalStorage, removeFromLocalStorage } = useLocalStorage();
  const defaultErrorMessage = 'Something went wrong with your import. Please reach out to the Vamos team if this error persists.';
  const batchLoadErrorMessage = 'Some records failed to load. Please refresh the page to try again.';

  // Fetch metadata + first batch of entries
  useEffect(() => {
    if (importId === undefined) return;
    getImport(importId, { limit: BATCH_SIZE, offset: 0 })
      .then((response) => response.json())
      .then((data) => {
        setImportedFile(data);
        setAllEntries(data.raw_data?.entries || []);
        setTotalEntries(data.raw_data?.total_entries ?? 0);
      })
      .catch((error) => {
        showToast({
          message: error.message || defaultErrorMessage,
          type: 'error',
        });
      });
  }, [importId, showToast]);

  // Auto-fetch remaining batches sequentially until all entries are loaded.
  // Retries up to 3 times with exponential backoff (1s, 2s, 4s) so a transient
  // network hiccup does not silently leave the list incomplete.
  useEffect(() => {
    if (!importedFile || isFetchingRef.current || totalEntries === 0 || allEntries.length >= totalEntries) {
      return undefined;
    }

    isFetchingRef.current = true;

    const fetchBatch = () => {
      getImport(importId, { limit: BATCH_SIZE, offset: allEntries.length })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .then((data) => {
          retryCountRef.current = 0;
          isFetchingRef.current = false;
          setAllEntries((prev) => [...prev, ...(data.raw_data?.entries || [])]);
        })
        .catch(() => {
          if (retryCountRef.current < 3) {
            retryCountRef.current += 1;
            retryTimeoutRef.current = setTimeout(fetchBatch, 1000 * (2 ** (retryCountRef.current - 1)));
          } else {
            retryCountRef.current = 0;
            isFetchingRef.current = false;
            showToast({
              message: batchLoadErrorMessage,
              type: 'error',
            });
          }
        });
    };

    fetchBatch();
    return () => clearTimeout(retryTimeoutRef.current);
  }, [importedFile, allEntries.length, totalEntries, importId, showToast]);

  // show error toast message after redirection during the errored import
  useEffect(() => {
    const toastMessage = getFromLocalStorage('toastMessageForError');
    removeFromLocalStorage('toastMessageForError');
    if (toastMessage) {
      showToast({ message: toastMessage, type: 'error' });
    }
  },
  [getFromLocalStorage, removeFromLocalStorage, showToast]);

  // handle the fetch the url to download the import file
  useEffect(() => {
    if (importId === undefined) return;
    downloadImportFile(importId, organizationId)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((data) => {
        if (!data) {
          throw new Error('Link to the file was not generated');
        }
        setHrefLink(data.download_url);
      })
      .catch(() => {
        // silent error because we do not dhow the link if the url did not generate
        return null;
      });
  },
  [importId, organizationId]);

  const handleDownloadFile = () => {
    if (hrefLink) {
      const link = document.createElement('a');
      link.href = hrefLink;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  return (
    <DialogsProvider>
      <>
        <header>
          <HeaderWithBreadcrumbs links={[{ href: '/imports', name: 'Imports' }, { href: `/imports/${importId}`, name: fileName }]} name="reports" label={fileName} />
        </header>
        <main>
          <ImportPage
            importedFile={importedFile}
            entries={allEntries}
            totalEntries={totalEntries}
            hrefLink={hrefLink}
            handleDownloadFile={handleDownloadFile}
          />
        </main>
      </>
    </DialogsProvider>
  );
};

ImportPageWrapper.propTypes = {
  importId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]).isRequired,
  filename: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]).isRequired,
  organizationId: PropTypes.number.isRequired,
};

export default withContextProvider(ToastProvider)(ImportPageWrapper);
